import React, { useState, useEffect, useRef } from 'react';
import { Globe, X } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/LanguageCurrencyModal.css';

const LanguageCurrencyModal = ({ isOpen, onClose, currentLanguage, currentCurrency, onLanguageChange, onCurrencyChange, supportedLanguages }) => {
  const modalRef = useRef(null);
  const { supportedCurrencies: currenciesFromContext } = useCurrency();
  const { t } = useTranslation();

  const languages = supportedLanguages || [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Español', nativeName: 'Español' },
    { code: 'fr', name: 'Français', nativeName: 'Français' },
    { code: 'de', name: 'Deutsch', nativeName: 'Deutsch' },
    { code: 'it', name: 'Italiano', nativeName: 'Italiano' },
    { code: 'pt', name: 'Português', nativeName: 'Português' },
    { code: 'ru', name: 'Русский', nativeName: 'Русский' },
    { code: 'zh-CN', name: '中文', nativeName: '中文' },
    { code: 'ja', name: '日本語', nativeName: '日本語' },
    { code: 'ko', name: '한국어', nativeName: '한국어' },
    { code: 'ar', name: 'العربية', nativeName: 'العربية' },
    { code: 'hi', name: 'हिन्दी', nativeName: 'हिन्दी' },
  ];

  // Use currencies from context or fallback to default list
  const currencies = currenciesFromContext || [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  ];

  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const languageDropdownRef = useRef(null);
  const currencyDropdownRef = useRef(null);

  useEffect(() => {
    setSelectedLanguage(currentLanguage);
    setSelectedCurrency(currentCurrency);
  }, [currentLanguage, currentCurrency, isOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setShowCurrencyDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSave = () => {
    onLanguageChange(selectedLanguage);
    onCurrencyChange(selectedCurrency);
    onClose();
  };

  const handleCancel = () => {
    setSelectedLanguage(currentLanguage);
    setSelectedCurrency(currentCurrency);
    onClose();
  };

  const selectedLanguageData = languages.find(lang => lang.code === selectedLanguage) || languages[0];
  const selectedCurrencyData = currencies.find(curr => curr.code === selectedCurrency) || currencies[1];

  if (!isOpen) return null;

  return (
    <div className="language-currency-modal-overlay" onClick={handleCancel}>
      <div className="language-currency-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <div className="language-currency-modal-handle"></div>
        <div className="language-currency-modal-header">
          <div className="modal-header-left">
            <Globe size={20} color="var(--text-primary)" />
            <h2>{t('settings.chooseLanguageCurrency') || 'Choose your language and currency'}</h2>
          </div>
          <button className="modal-close-btn" onClick={handleCancel}>
            <X size={20} color="var(--text-primary)" />
          </button>
        </div>

        <div className="language-currency-modal-content">
          <div className="modal-field-group">
            <label htmlFor="language-select">{t('settings.languageLabel')}</label>
            <div className="modal-dropdown-wrapper" ref={languageDropdownRef}>
              <button
                className="modal-dropdown-btn"
                onClick={() => {
                  setShowLanguageDropdown(!showLanguageDropdown);
                  setShowCurrencyDropdown(false);
                }}
              >
                <span>{selectedLanguageData.nativeName} ({selectedLanguageData.code.toUpperCase()})</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showLanguageDropdown && (
                <div className="modal-dropdown-menu">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`modal-dropdown-item ${selectedLanguage === lang.code ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedLanguage(lang.code);
                        setShowLanguageDropdown(false);
                      }}
                    >
                      <span>{lang.nativeName}{lang.code !== 'en' && ` (${lang.code.toUpperCase()})`}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-field-group">
            <label htmlFor="currency-select">{t('settings.currencyLabel')}</label>
            <div className="modal-dropdown-wrapper" ref={currencyDropdownRef}>
              <button
                className="modal-dropdown-btn"
                onClick={() => {
                  setShowCurrencyDropdown(!showCurrencyDropdown);
                  setShowLanguageDropdown(false);
                }}
              >
                <span>{selectedCurrencyData.code} - {selectedCurrencyData.symbol}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showCurrencyDropdown && (
                <div className="modal-dropdown-menu">
                  {currencies.map((curr) => (
                    <button
                      key={curr.code}
                      className={`modal-dropdown-item ${selectedCurrency === curr.code ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCurrency(curr.code);
                        setShowCurrencyDropdown(false);
                      }}
                    >
                      <span>{curr.code} - {curr.symbol}</span>
                      <span className="currency-name">{curr.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="language-currency-modal-actions">
          <button className="modal-btn-save" onClick={handleSave}>
            {t('common.save')}
          </button>
          <button className="modal-btn-cancel" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageCurrencyModal;

